const PickerProvider = require('./PictProvider-InputType-Picker.js');
const ScopedEntityData = require('./_ScopedEntityData.js');
const EntityDataBaseline = require('./_EntityDataBaseline.js');
const libSeedRecord = require('./_SeedRecord.js');

const SALT_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
// Special committed value marking "create a new entity" — same sentinel the legacy MES used, so the
// synthesis path is diff-reviewable against it. Committed by the PSP pinned "New <Entity>..." create row (OnCreate).
const NEW_ENTITY_ID = '__NEW__';

/** @type {Record<string, any>} */
const _DEFAULT_CONFIGURATION =
{
	ProviderIdentifier: 'MaterialsCascadePicker',
	AutoInitialize: true,
	AutoInitializeOrdinal: 0,
	AutoSolveWithApp: false,
};

/**
 * The intermediate broker base for the MaterialsEntitySelector family port (beast §8.8, architecture A):
 * `Picker` (config, `__Record`, filters, defaults) → `MaterialsCascadePicker` (THIS — the shared cascade
 * infrastructure the legacy MES base carried) → the bespoke `Materials*Picker` subclasses, each mirroring
 * its MES counterpart 1:1 for diff-review.
 *
 * Shared infrastructure ported from the MES base:
 *   - the cross-widget EVENT BUS fan-out (`<Entity>Changed:<trackingGUID>:<recordAddress>` +
 *     `FireTriggerGroupOnRecordRefresh`), fired from `_onEntityRecordCascade` — the broker seam that
 *     replaces the legacy `_onRecordRefresh` — and received via onAfterEvent(Tabular)Completion.
 *   - tracking-GUID plumbing (`MATERIALS-ENTITY-TRACKING-GUID-…` — SAME element id as the legacy
 *     templates, so still-legacy sibling selectors keep binding from a migrated Sample during the
 *     phased rollout).
 *   - `__Salt` (record-uniqueness salt kept beside `__Record`) + record-template translation.
 *   - the creation-GUID record identity (`GUID<Entity>` when creation is enabled) + the System-1
 *     local-record resolver, which doubles as the `_refreshEntityRecord` CLOBBER-GUARD: a synthesized
 *     unsaved record would miss every server lookup, and the guard keeps the snapshot without a
 *     guaranteed-miss round trip.
 */
class PictProviderInputTypeMaterialsCascadePicker extends PickerProvider
{
	/**
	 * @param {Object} pFable @param {Object} [pOptions] @param {string} [pServiceHash]
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({}, _DEFAULT_CONFIGURATION, pOptions), pServiceHash);
		// Inherited fable-service members the duplicate-pict-provider type chain hides from tsc.
		/** @type {Record<string, any>} */
		this.options;
		/** @type {any} */
		this.log;
	}

	/**
	 * The pinned "New <Entity>..." row of a creation-enabled picker: always at the top of the open list,
	 * no typing needed, as the legacy pinned row (GetPinnedItem) was. Choosing it commits the
	 * NEW_ENTITY_ID sentinel, which onDataChange rewrites into a synthesized record; any typed term is
	 * ignored, as the legacy row ignored it.
	 * @param {Record<string, any>} pConfig - The picker config, given its Entity.
	 */
	_applyNewEntityRow(pConfig)
	{
		const tmpLabel = `New ${pConfig.Entity}...`;
		pConfig.OnCreate = () => ({ Value: NEW_ENTITY_ID, Text: tmpLabel });
		pConfig.CreateAlwaysVisible = true;
		pConfig.CreateLabel = tmpLabel;
	}

	/**
	 * Whether entity creation is enabled for this input — the per-input PictForm flag, falling back to
	 * the provider option the creating subclasses set. Latches a provider-level flag so the save-time
	 * EntityData projection fires even on a form where no sibling selector owns that trigger.
	 * Ported from the MES base.
	 * @param {Record<string, any>} pInput @return {boolean}
	 */
	_isEntityCreationEnabled(pInput)
	{
		const tmpEnabled = ((pInput?.PictForm?.AllowEntityCreation ?? this.options.AllowEntityCreation) === true);
		if (tmpEnabled)
		{
			this._entityCreationEnabled = true;
		}
		return tmpEnabled;
	}

	/**
	 * The record identity: a creation-enabled selector tracks records by `GUID<Entity>` (a new record
	 * has no numeric `ID<Entity>` yet) — the MES `_getDefaultRecordIdentity` branch layered onto the
	 * base broker's RecordIdentity/IdentityField precedence.
	 * @param {Record<string, any>} pInput @return {string}
	 */
	_getRecordIdentity(pInput)
	{
		const tmpPictForm = (pInput && pInput.PictForm) || {};
		if (tmpPictForm.RecordIdentity || tmpPictForm.IdentityField)
		{
			return super._getRecordIdentity(pInput);
		}
		const tmpEntity = this._resolveEntity(tmpPictForm);
		if (this._isEntityCreationEnabled(pInput))
		{
			return `GUID${tmpEntity}`;
		}
		return `ID${tmpEntity}`;
	}

	/**
	 * Carry the cascade identity into the picker config: a creation-enabled field commits its GUID
	 * identity, so PSP's ValueField must match what the legacy select2 committed (GetRecordID read
	 * `_getRecordIdentity` too).
	 * @param {Record<string, any>} pInput @param {string} pHostSelector @param {Function} fOnChange
	 * @return {Record<string, any>}
	 */
	_buildPickerConfig(pInput, pHostSelector, fOnChange)
	{
		const tmpConfig = super._buildPickerConfig(pInput, pHostSelector, fOnChange);
		if (tmpConfig.Entity)
		{
			const tmpIdentity = this._getRecordIdentity(pInput);
			if (!((pInput.PictForm || {}).IdentityField) && tmpIdentity !== `ID${tmpConfig.Entity}`)
			{
				tmpConfig.ValueField = tmpIdentity;
			}
			if (this._isEntityCreationEnabled(pInput) && !tmpConfig.OnCreate)
			{
				this._applyNewEntityRow(tmpConfig);
			}
			// A distilled field lists its Dynamic picklist, as MaterialsEntitySelector's dynamicLocalPaginationAdapter did.
			const fPickListDataProvider = this._createDynamicPickListDataProvider(pInput);
			if (fPickListDataProvider)
			{
				tmpConfig.PickListDataProvider = fPickListDataProvider;
			}
			if (typeof tmpConfig.ResolveValue !== 'function')
			{
				const tmpMountContext = this._activeTabularContext;
				const tmpMountView = tmpMountContext ? tmpMountContext.pView : null;
				const tmpMountRow = (tmpMountContext && tmpMountContext.pRowIndex != null) ? tmpMountContext.pRowIndex : null;
				tmpConfig.ResolveValue = (pValue) => this._resolveHeldOrEntityOption(tmpConfig, pInput, tmpMountView, tmpMountRow, pValue);
			}
		}
		return tmpConfig;
	}

	/**
	 * Label a saved value from the `__Record` the form holds, as MaterialsEntitySelector did (its local-record render),
	 * so a record the server cannot return (offline, deleted, unreadable) still reads by name and a re-render costs no
	 * read; a value the form holds no record for is read the way pict-section-picker reads any value.
	 * @param {Record<string, any>} pConfig @param {Record<string, any>} pInput @param {Object|null} pView
	 * @param {number|string|null} pRowIndex @param {any} pValue
	 * @return {Promise<Record<string, any>|null>} the option, or null.
	 */
	_resolveHeldOrEntityOption(pConfig, pInput, pView, pRowIndex, pValue)
	{
		const tmpIdentity = this._getRecordIdentity(pInput);
		if (this._findHeldRecord(pView, pInput, tmpIdentity, pValue, pRowIndex))
		{
			return this._resolveIdentityOption(pConfig, pInput, tmpIdentity, pView, pRowIndex, pValue);
		}
		return Promise.resolve(this.pict.providers['Pict-Section-Picker'].createEntityResolveValue(pConfig)(pValue));
	}

	/**
	 * Mount the entity picker, then page a Dynamic picklist in place of the entity search when the config carries
	 * one. The saved value still resolves through the entity read, so it labels from its record.
	 * @param {string} pPickerHash @param {Record<string, any>} pConfig @return {any} The picker view.
	 */
	_instantiatePicker(pPickerHash, pConfig)
	{
		const tmpView = super._instantiatePicker(pPickerHash, pConfig);
		if (tmpView?.options && pConfig && (typeof pConfig.PickListDataProvider === 'function'))
		{
			tmpView.options.DataProvider = pConfig.PickListDataProvider;
		}
		return tmpView;
	}

	/**
	 * Leave out a tag that reads the same as the option's label, as the Select2 family does (Select2Util's
	 * option text, MaterialsEntitySelector._formatRecordForDisplay): every Lab picker is tagged on the Lab's own
	 * Name, and a Sample created on a form is named by its QR code.
	 *
	 * @param {Record<string, any>} pOption - a picker option `{ Value, Text, Record[, Tag][, Tags] }`.
	 * @param {Record<string, any>} pConfig - the picker config.
	 * @return {Record<string, any>} The option, or a copy of it.
	 */
	_presentEntityOption(pOption, pConfig)
	{
		const tmpOption = super._presentEntityOption(pOption, pConfig);
		if (!tmpOption || (typeof tmpOption !== 'object') || tmpOption.Tag === undefined || tmpOption.Tag === null)
		{
			return tmpOption;
		}
		const tmpText = (tmpOption.Text === undefined || tmpOption.Text === null) ? '' : String(tmpOption.Text);
		if (String(tmpOption.Tag).trim() !== tmpText.trim())
		{
			return tmpOption;
		}
		const tmpPresented = Object.assign({}, tmpOption);
		delete tmpPresented.Tag;
		return tmpPresented;
	}

	// --- Creation primitives (ported from the MES base _getDefaultRecord/_handleDefaultRecord) ---

	/**
	 * The record to synthesize for a new entity: the descriptor's `DefaultRecord`, plus — when creation
	 * is enabled and not opted out via `GenerateGUID: false` — a client `GUID<Entity>` minted from the
	 * document GUID + a per-input stanza (`GUIDEntityTypeStanza`, e.g. the Sample subclass's 'S') + the
	 * `__Salt`. Raw template tokens; translated downstream in _handleDefaultRecord.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} [pRowIndex]
	 * @return {Record<string, any>|undefined}
	 */
	_getDefaultRecord(pView, pInput, pRowIndex)
	{
		let tmpRecord = pInput.PictForm?.DefaultRecord;
		if (this._isEntityCreationEnabled(pInput) && pInput.PictForm?.GenerateGUID !== false)
		{
			const tmpEntity = this._resolveEntity((pInput && pInput.PictForm) || {});
			const tmpGUIDField = `GUID${tmpEntity}`;
			tmpRecord = (tmpRecord && typeof tmpRecord === 'object' && !Array.isArray(tmpRecord)) ? JSON.parse(JSON.stringify(tmpRecord)) : { };
			if (!tmpRecord[tmpGUIDField])
			{
				const tmpStanza = pInput.PictForm?.GUIDEntityTypeStanza || tmpEntity;
				tmpRecord[tmpGUIDField] = `{~D:AppData.DocumentData.ReportData.GUIDDocument~}-${tmpStanza}-${this._getSalt(pView, pInput, pRowIndex)}`;
			}
		}
		return tmpRecord;
	}

	/**
	 * Synthesize the new record: deep-copy + translate the template tokens, land the record at
	 * `__Record` and its identity as the committed value, and return that value. The subsequent record
	 * refresh hits the System-1 local-record short-circuit (an unsaved GUID would miss every server
	 * lookup), so the cascade fires with the synthesized record and nothing clobbers it.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {any} pValue
	 * @param {string} [pTransactionGUID] @param {number|string|null} [pRowIndex]
	 * @return {any} the synthesized identity value (or null when nothing synthesized)
	 */
	_handleDefaultRecord(pView, pInput, pValue, pTransactionGUID, pRowIndex)
	{
		let tmpDefaultRecord = this._getDefaultRecord(pView, pInput, pRowIndex);
		if (tmpDefaultRecord)
		{
			tmpDefaultRecord = JSON.parse(JSON.stringify(tmpDefaultRecord));
			this._translateRecordTemplateFields(tmpDefaultRecord);
			const tmpDestination = pView.getMarshalDestinationObject();
			const tmpSeedRecord = libSeedRecord.getUnsavedSeedRecord(
				this.pict.manifest.getValueAtAddress(tmpDestination, this._getRecordAddress(pInput, pRowIndex)),
				this._resolveEntity((pInput && pInput.PictForm) || {}), this._getRecordIdentity(pInput));
			if (tmpSeedRecord)
			{
				tmpDefaultRecord = Object.assign(tmpDefaultRecord, tmpSeedRecord);
			}
			const tmpDefaultID = tmpDefaultRecord[this._getRecordIdentity(pInput)];
			pValue = tmpDefaultID || null;
			if (pValue)
			{
				this.pict.manifest.setValueAtAddress(tmpDestination, this._getRecordAddress(pInput, pRowIndex, ''), pValue);
			}
			this.pict.manifest.setValueAtAddress(tmpDestination, this._getRecordAddress(pInput, pRowIndex), tmpDefaultRecord);
			// A record created here has no server row yet, so this open never needs to read it.
			const tmpEntity = this._resolveEntity((pInput && pInput.PictForm) || {});
			if (pValue && !(Number(tmpDefaultRecord[`ID${tmpEntity}`]) > 0))
			{
				this._openRefreshes().add(this._openRefreshKey(pInput, pValue, pRowIndex));
			}
		}
		return pValue;
	}

	/**
	 * Intercept the NEW_ENTITY_ID commit (the pinned "New <Entity>..." row): force a fresh salt, synthesize the
	 * record, rewrite the committed value to its identity in the DOM (mid provider-loop — no second
	 * dataChanged), then continue the standard path: the base's record refresh short-circuits on the
	 * local record and fires the cascade fenced.
	 */
	onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this._handleSelectionRemovals(pView, pInput, pValue, null);
		if (pValue === NEW_ENTITY_ID)
		{
			this._generateSaltIfNeeded(pView, pInput, null, true);
			pValue = this._handleDefaultRecord(pView, pInput, pValue, pTransactionGUID, null);
			this._syncCommittedValueDOM(pInput, pValue, null);
		}
		else if (this._isClearedPickerValue(pValue))
		{
			this._handleEntitySelectionCleared(pView, pInput, null);
		}
		return super.onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/** Tabular twin of the NEW_ENTITY_ID intercept. */
	onDataChangeTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this._handleSelectionRemovals(pView, pInput, pValue, pRowIndex);
		if (pValue === NEW_ENTITY_ID)
		{
			this._generateSaltIfNeeded(pView, pInput, pRowIndex, true);
			pValue = this._handleDefaultRecord(pView, pInput, pValue, pTransactionGUID, pRowIndex);
			this._syncCommittedValueDOM(pInput, pValue, pRowIndex);
		}
		else if (this._isClearedPickerValue(pValue))
		{
			this._handleEntitySelectionCleared(pView, pInput, pRowIndex);
		}
		return super.onDataChangeTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * True when init must SYNTHESIZE a record rather than hydrate a stored one: a MaterialsHidden
	 * InputType or a `DefaultToNewRecord` field with no value auto-creates, and a persisted
	 * NEW_ENTITY_ID token re-synthesizes.
	 * @param {Record<string, any>} pInput @param {any} pValue @return {boolean}
	 */
	_isInitSynthesisValue(pInput, pValue)
	{
		const tmpInputType = pInput?.PictForm?.InputType || '';
		if (pValue === NEW_ENTITY_ID)
		{
			return true;
		}
		return !pValue && (tmpInputType.startsWith('MaterialsHidden') || !!pInput?.PictForm?.DefaultToNewRecord);
	}

	/**
	 * True when init carries a stored value to hydrate. Uses the same split `_refreshEntityRecord`
	 * decides "cleared" on, so the load path can never take that method's destructive clear branch —
	 * init must not blank a `__Record` slot it was not asked to fill.
	 * @param {Record<string, any>} pInput @param {any} pValue @return {boolean}
	 */
	_hasInitValueToHydrate(pInput, pValue)
	{
		return this._splitPickerValues(pValue, pInput).length > 0;
	}

	/**
	 * Init-time creation (ported) OR load-time hydration. The synthesis branch auto-creates the record;
	 * every other init routes the STORED value through the record refresh, which is the family's only
	 * cascade seam — `MaterialsEntitySelector.onInputInitialize` refreshes unconditionally, and without
	 * the same call here opening a saved document runs no DocumentPolyJoin synthesis, no `<Entity>Changed`
	 * fan-out, no spec-scope prime and no join reconciliation. The local-record short-circuit answers a
	 * matching persisted snapshot without a fetch, so the hydration costs a cascade, not a round trip.
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this._generateSaltIfNeeded(pView, pInput, null);
		this._scheduleBaselineCapture(pView, pTransactionGUID);
		const tmpResult = super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		if (this._isInitSynthesisValue(pInput, pValue))
		{
			const tmpValue = this._handleDefaultRecord(pView, pInput, pValue, pTransactionGUID, null);
			if (tmpValue)
			{
				this._syncCommittedValueDOM(pInput, tmpValue, null);
				this._refreshEntityRecord(pView, pInput, tmpValue, pTransactionGUID, null);
			}
		}
		else if (this._hasInitValueToHydrate(pInput, pValue))
		{
			this._markReinitOfFiredTriggerGroup(pInput, pValue, null);
			this._refreshEntityRecord(pView, pInput, pValue, pTransactionGUID, null);
		}
		return tmpResult;
	}

	/** Tabular twin of the init-time creation and load-time hydration. */
	onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this._generateSaltIfNeeded(pView, pInput, pRowIndex);
		this._scheduleBaselineCapture(pView, pTransactionGUID);
		const tmpResult = super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
		if (this._isInitSynthesisValue(pInput, pValue))
		{
			const tmpValue = this._handleDefaultRecord(pView, pInput, pValue, pTransactionGUID, pRowIndex);
			if (tmpValue)
			{
				this._syncCommittedValueDOM(pInput, tmpValue, pRowIndex);
				this._refreshEntityRecord(pView, pInput, tmpValue, pTransactionGUID, pRowIndex);
			}
		}
		else if (this._hasInitValueToHydrate(pInput, pValue))
		{
			this._markReinitOfFiredTriggerGroup(pInput, pValue, pRowIndex);
			this._refreshEntityRecord(pView, pInput, pValue, pTransactionGUID, pRowIndex);
		}
		return tmpResult;
	}

	/**
	 * Whether the document is frozen (read-only / Locked): the same signal the entity selector gates refresh on.
	 * @return {boolean}
	 */
	_isRecordFrozen()
	{
		return !this.pict?.AppData?.Interactive || !!this.pict?.AppData?.DocumentData?.ReportData?.Locked;
	}

	/**
	 * Whether a sample-join refresh may rewrite this input's joins. A frozen document keeps the joins it
	 * saved; an input that must follow the database even then sets `PictForm.RefreshJoinsWhenLocked: true`.
	 * @param {Record<string, any>} pInput
	 * @return {boolean}
	 */
	_shouldRefreshSampleJoins(pInput)
	{
		return !this._isRecordFrozen() || pInput?.PictForm?.RefreshJoinsWhenLocked === true;
	}

	/**
	 * The empty-result branch of a sample-join refresh: drop the held joins whose rows were soft-deleted
	 * since the document was saved, and their values from the selection. Only a row read back as deleted
	 * is dropped (`ScopedEntityData.resolveSoftDeletedJoins`); anything else keeps the persisted value. The
	 * drop is a load, so no deletion is staged, and it re-salts the input so a re-add mints a GUID the
	 * deleted row does not own. An input with `RefreshOnOpen: false` keeps its saved snapshot.
	 *
	 * The drop is applied to the joins and value the input holds when the reads settle
	 * (`ScopedEntityData.applySoftDeletedDrop`), not to the ones it started from, so a refresh that landed
	 * meanwhile is not undone and an overlapping drop that already removed the links writes nothing.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} pRowIndex
	 * @param {{ Entity: string, ValueField: string, JoinAddress: string, Value: any, Label: string }} pJoinScope
	 * @param {(pDropped: number) => void} fDone - Called once, after any write, with the number of held links
	 *   found deleted.
	 */
	_dropSoftDeletedHeldJoins(pView, pInput, pRowIndex, pJoinScope, fDone)
	{
		if (pInput.PictForm?.RefreshOnOpen === false)
		{
			return fDone(0);
		}
		const tmpHeldJoins = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), pJoinScope.JoinAddress);
		ScopedEntityData.resolveSoftDeletedJoins(this.pict, pJoinScope.Entity, pJoinScope.ValueField, tmpHeldJoins, pJoinScope.Value, (pResult) =>
		{
			if (pResult.Dropped.length < 1)
			{
				return fDone(0);
			}
			const tmpDestination = pView.getMarshalDestinationObject();
			const tmpDrop = ScopedEntityData.applySoftDeletedDrop(pJoinScope.Entity, pJoinScope.ValueField,
				this.pict.manifest.getValueAtAddress(tmpDestination, pJoinScope.JoinAddress),
				this.pict.manifest.getValueAtAddress(tmpDestination, this._getPickerValueAddress(pInput, pRowIndex)),
				pResult);
			if (!tmpDrop.Changed)
			{
				return fDone(pResult.Dropped.length);
			}
			ScopedEntityData.beginProgrammaticLoad(this.pict);
			try
			{
				if (tmpDrop.Removed.length > 0)
				{
					this.pict.manifest.setValueAtAddress(tmpDestination, pJoinScope.JoinAddress, tmpDrop.Joins);
				}
				const tmpRecordAddress = this._getRecordAddress(pInput, pRowIndex);
				const tmpHeldRecords = this.pict.manifest.getValueAtAddress(tmpDestination, tmpRecordAddress);
				const tmpKeptRecords = ScopedEntityData.pruneRecordsToValue(tmpHeldRecords, pJoinScope.ValueField, tmpDrop.Value);
				if (tmpKeptRecords !== tmpHeldRecords)
				{
					this.pict.manifest.setValueAtAddress(tmpDestination, tmpRecordAddress, tmpKeptRecords);
				}
				if (tmpDrop.ValueChanged)
				{
					this._setValueAndCommit(pView, pInput, tmpDrop.Value, pRowIndex);
				}
			}
			finally
			{
				ScopedEntityData.endProgrammaticLoad(this.pict);
			}
			this._generateSaltIfNeeded(pView, pInput, pRowIndex, true);
			this.log.info(`${pJoinScope.Label}: dropped ${tmpDrop.Removed.length} link(s) whose record was deleted since the document was saved: ${pResult.Dropped.join(', ')}; value now [${tmpDrop.Value}].`);
			return fDone(pResult.Dropped.length);
		});
	}

	// --- __Salt + record-template translation (ported from the MES base) ---

	/** @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} [pRowIndex] @param {boolean} [pForce] */
	_generateSaltIfNeeded(pView, pInput, pRowIndex, pForce)
	{
		const tmpSaltAddress = this._getRecordAddress(pInput, pRowIndex, '__Salt');
		const tmpSalt = !pForce && this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpSaltAddress);
		if (!tmpSalt)
		{
			this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpSaltAddress, this._generateSaltStr());
		}
	}

	/** @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} [pRowIndex] @return {string} */
	_getSalt(pView, pInput, pRowIndex)
	{
		return this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), this._getRecordAddress(pInput, pRowIndex, '__Salt'));
	}

	/** @param {number} [pLength=8] @return {string} */
	_generateSaltStr(pLength = 8)
	{
		let tmpResult = '';
		for (let i = 0; i < pLength; i++)
		{
			tmpResult += SALT_CHARS.charAt(Math.floor(Math.random() * SALT_CHARS.length));
		}
		return tmpResult;
	}

	/** Translate `{~D:…~}` template tokens in every string field of a synthesized record (in place). */
	_translateRecordTemplateFields(pRecord)
	{
		for (const tmpField in pRecord)
		{
			if (typeof pRecord[tmpField] === 'string')
			{
				pRecord[tmpField] = this.pict.parseTemplate(pRecord[tmpField], this.pict);
			}
		}
	}

	// --- Tracking-GUID plumbing (ported; SAME element ids as the legacy templates) ---

	/** @param {string} pInputHTMLID @param {number|string|null} [pRowIndex] @param {string} [pType] @return {string} */
	getTrackingGUIDID(pInputHTMLID, pRowIndex, pType)
	{
		const tmpType = pType ? `${pType}-` : '';
		if (pRowIndex != null)
		{
			return `#MATERIALS-ENTITY-TRACKING-GUID-${tmpType}${pInputHTMLID}-${pRowIndex}`;
		}
		return `#MATERIALS-ENTITY-TRACKING-GUID-${tmpType}${pInputHTMLID}`;
	}

	/** @param {Record<string, any>} pInput @param {number|string|null} [pRowIndex] @param {string} [pType] @return {string} */
	_getTrackingGUID(pInput, pRowIndex, pType)
	{
		if (pRowIndex == null)
		{
			switch (pType)
			{
			case 'TEST':
				if (pInput.PictForm?.TestTrackingGUID && pInput.PictForm.TestTrackingGUID != true)
				{
					return /** @type {string} */ (pInput.PictForm.TestTrackingGUID);
				}
			// eslint-disable-next-line no-fallthrough
			default:
				if (pInput.PictForm?.SampleTrackingGUID && pInput.PictForm.SampleTrackingGUID != true)
				{
					return /** @type {string} */ (pInput.PictForm.SampleTrackingGUID);
				}
			}
		}
		return /** @type {string} */ (this.pict.ContentAssignment.readContent(this.getTrackingGUIDID(pInput.Macro.RawHTMLID, pRowIndex, pType)));
	}

	/** @param {Record<string, any>} pInput @param {number|string|null} pRowIndex @param {string} pTrackingGUID @param {string} [pType] */
	_setTrackingGUID(pInput, pRowIndex, pTrackingGUID, pType)
	{
		this.pict.ContentAssignment.assignContent(this.getTrackingGUIDID(pInput.Macro.RawHTMLID, pRowIndex, pType), pTrackingGUID);
	}

	/**
	 * Bind a NON-Sample cascade selector's tracking GUID from its sibling Sample descriptor (tabular
	 * group first, then the global manifest) — the sample owns the family's tracking identity.
	 * Ported from the MES base; fired from the marshal hooks like the legacy.
	 * @param {Object} pView @param {Object} pGroup @param {Record<string, any>} pInput @param {number|string|null} [pRowIndex]
	 */
	_bindTrackingGUIDIfNeeded(pView, pGroup, pInput, pRowIndex)
	{
		if (this._getTrackingGUID(pInput, pRowIndex))
		{
			return;
		}
		if (this._resolveEntity((pInput && pInput.PictForm) || {}) == 'Sample')
		{
			return;
		}
		if (pRowIndex != null)
		{
			const tmpLocalManifest = pGroup && pGroup.supportingManifest;
			if (!tmpLocalManifest || typeof tmpLocalManifest !== 'object' || Array.isArray(tmpLocalManifest))
			{
				this.log.error('MaterialsCascadePicker: invalid supporting manifest on group.', { pGroup });
			}
			else
			{
				for (const tmpDescriptor of Object.values(tmpLocalManifest.elementDescriptors))
				{
					if (tmpDescriptor.PictForm && tmpDescriptor.PictForm.SampleTrackingGUID)
					{
						const tmpTrackingGUID = this._getTrackingGUID(tmpDescriptor, pRowIndex);
						if (tmpTrackingGUID)
						{
							this._setTrackingGUID(pInput, pRowIndex, tmpTrackingGUID);
							return;
						}
					}
				}
			}
		}
		const tmpGlobalManifest = this.pict.views.PictFormMetacontroller?.manifestDescription;
		if (!tmpGlobalManifest || !tmpGlobalManifest.Descriptors || typeof tmpGlobalManifest.Descriptors !== 'object' || Array.isArray(tmpGlobalManifest.Descriptors))
		{
			this.log.error('MaterialsCascadePicker: invalid global manifest on metacontroller.', { pGroup });
			return;
		}
		for (const tmpDescriptor of Object.values(tmpGlobalManifest.Descriptors))
		{
			if (tmpDescriptor.PictForm && tmpDescriptor.PictForm.SampleTrackingGUID)
			{
				const tmpTrackingGUID = this._getTrackingGUID(tmpDescriptor, null);
				if (tmpTrackingGUID)
				{
					this._setTrackingGUID(pInput, pRowIndex, tmpTrackingGUID);
					return;
				}
			}
		}
		if (!this._getTrackingGUID(pInput, pRowIndex))
		{
			this.log.warn('MaterialsCascadePicker: could not find sibling sample descriptor to bind tracking GUID from.');
		}
	}

	/** Bind the tracking GUID and bring the value's record on marshal, like the legacy MES onDataMarshalToForm did. */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this._bindTrackingGUIDIfNeeded(pView, pGroup, pInput, null);
		// @ts-ignore — inherited through the PSP chain tsc cannot see (duplicate pict-provider types).
		const tmpResult = super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		this._backfillRecordOnMarshal(pView, pInput, pValue, null);
		return tmpResult;
	}

	/** Tabular twin of the marshal-time tracking-GUID bind and record backfill. */
	onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this._bindTrackingGUIDIfNeeded(pView, pGroup, pInput, pRowIndex);
		// @ts-ignore — inherited through the PSP chain tsc cannot see.
		const tmpResult = super.onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
		this._backfillRecordOnMarshal(pView, pInput, pValue, pRowIndex);
		return tmpResult;
	}

	/**
	 * When a marshalled single value (a solver or an autofill wrote it) is not the entity its held `__Record` holds,
	 * read the value's row into `__Record`, as `MaterialsEntitySelector._backfillMissingRecords` did on marshal: the
	 * record only, with no cascade, so a solver reading `__Record` computes from the row the field shows. A frozen
	 * document keeps its saved record, a failed read keeps what is held, and a read that lands after the value moved
	 * on writes nothing.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {any} pValue @param {number|string|null} pRowIndex
	 * @return {Promise<void>|void}
	 */
	_backfillRecordOnMarshal(pView, pInput, pValue, pRowIndex)
	{
		const tmpPictForm = (pInput && pInput.PictForm) || {};
		if (this._isRecordFrozen() || this._isMultiplePicker(tmpPictForm) || !pView || (typeof pView.getMarshalDestinationObject !== 'function'))
		{
			return;
		}
		const tmpValues = this._splitPickerValues(pValue, pInput);
		if (tmpValues.length !== 1 || tmpValues[0] === NEW_ENTITY_ID || this._getLocalRecordForValue(pView, pInput, tmpValues[0], pRowIndex))
		{
			return;
		}
		const tmpEntity = this._resolveEntity(tmpPictForm);
		const tmpKey = this._openRefreshKey(pInput, tmpValues[0], pRowIndex);
		if (!tmpEntity || this._openRefreshes().has(tmpKey))
		{
			return;
		}
		this._openRefreshes().add(tmpKey);
		const tmpValueAddress = (pRowIndex != null) ?
			this.pict.providers.Informary.getComposedContainerAddress(tmpPictForm.InformaryContainerAddress, pRowIndex, tmpPictForm.InformaryDataAddress) :
			(pInput.DataAddress || pInput.Hash);
		const tmpIdentity = this._getRecordIdentity(pInput);
		return this._resolveEntityRecord(tmpEntity, tmpValues[0], pInput).then((pRecord) =>
		{
			const tmpDestination = pView.getMarshalDestinationObject();
			const tmpCurrentValue = this._splitPickerValues(this.pict.manifest.getValueAtAddress(tmpDestination, tmpValueAddress), pInput);
			if (!pRecord || (pRecord[tmpIdentity] == null) || (tmpCurrentValue.length !== 1) || (tmpCurrentValue[0] !== tmpValues[0]))
			{
				return;
			}
			this.pict.manifest.setValueAtAddress(tmpDestination, this._getRecordAddress(pInput, pRowIndex), pRecord);
		}).catch((pError) =>
		{
			this.pict.log.warn(`Picker [${pInput.Hash}]: marshal record backfill failed for [${tmpValues[0]}]: ${pError.message || pError}`);
		});
	}

	// --- System-1 local-record resolve + the __Record clobber-guard ---

	/**
	 * The locally-held record for a value — the persisted `__Record` snapshot or a freshly synthesized
	 * record — when its IDENTITY MATCHES the committed value. Returns a shallow copy, or null when
	 * System 2 must load.
	 *
	 * REASONED DIVERGENCE from the MES base (live-found 2026-07-17, the B2 clear-reset family): the
	 * legacy carried a second keep-branch — "unsaved record (client identity, no server id) → retain" —
	 * which returned the SYNTHESIZED record even after the user picked a DIFFERENT entity, so the
	 * cascade (DocumentPolyJoin, SampleChanged fan-out) ran against the phantom created record. The
	 * branch was creation-flow armor the port does not need: every creation path (the NEW_ENTITY_ID
	 * intercepts and the init auto-create) rewrites the committed value to the synthesized identity
	 * BEFORE the record refresh runs, so the identity-match branch already covers creation — a mismatch
	 * can only mean the value moved on, exactly when the snapshot must NOT be kept.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {any} pValue @param {number|string|null} [pRowIndex]
	 * @return {Record<string, any>|null}
	 */
	_getLocalRecordForValue(pView, pInput, pValue, pRowIndex)
	{
		const tmpLocalRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), this._getRecordAddress(pInput, pRowIndex));
		if (tmpLocalRecord && typeof tmpLocalRecord === 'object' && !Array.isArray(tmpLocalRecord))
		{
			const tmpIdentity = this._getRecordIdentity(pInput);
			if (tmpLocalRecord[tmpIdentity] == pValue)
			{
				return Object.assign({}, tmpLocalRecord);
			}
		}
		return null;
	}

	/**
	 * CLOBBER-GUARD + System-1 short-circuit over the base record refresh: when the committed (single)
	 * value is already answered by the local `__Record` and `_keepsHeldRecord` allows it — a frozen
	 * document, `RefreshOnOpen: false`, a freshly synthesized unsaved record whose GUID would miss every
	 * server lookup, or a value this document open has already read — keep it and fire the cascade seam
	 * directly. Everything else (clears, multi, a value to read) delegates to the base, which reconciles
	 * the row through `_reconcileRefreshedRecord`.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {any} pValue
	 * @param {string} [pTransactionGUID] @param {number|string|null} [pRowIndex]
	 * @return {Promise<any>|void}
	 */
	_refreshEntityRecord(pView, pInput, pValue, pTransactionGUID, pRowIndex)
	{
		const tmpValues = this._splitPickerValues(pValue, pInput);
		if (tmpValues.length === 1 && !this._isMultiplePicker(pInput.PictForm) && pView && typeof pView.getMarshalDestinationObject === 'function')
		{
			const tmpLocalRecord = this._getLocalRecordForValue(pView, pInput, tmpValues[0], pRowIndex);
			if (tmpLocalRecord && this._keepsHeldRecord(pInput, tmpLocalRecord, tmpValues[0], pRowIndex))
			{
				// The snapshot at the address IS the record (the copy protects the caller); fence the
				// cascade exactly like the base select path so async cascade writes land pre-PostSolvers.
				const tmpRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), this._getRecordAddress(pInput, pRowIndex));
				const tmpOperationHash = `PickerRecordLocal-${this._getRecordAddress(pInput, pRowIndex)}-${this.pict.getUUID()}`;
				const tmpFenced = !!pTransactionGUID && typeof pView.registerEventTransactionAsyncOperation === 'function';
				if (tmpFenced)
				{
					pView.registerEventTransactionAsyncOperation(pTransactionGUID, tmpOperationHash);
				}
				return Promise.resolve()
					.then(() => this._onEntityRecordCascade(pView, pInput, tmpRecord, pRowIndex, pTransactionGUID))
					.catch((pError) =>
					{
						this.pict.log.error(`Picker [${pInput.Hash}]: error in local-record cascade: ${pError.message || pError}`);
					})
					.finally(() =>
					{
						if (tmpFenced)
						{
							pView.eventTransactionAsyncOperationComplete(pTransactionGUID, tmpOperationHash);
						}
					});
			}
			this._openRefreshes().add(this._openRefreshKey(pInput, tmpValues[0], pRowIndex));
		}
		return super._refreshEntityRecord(pView, pInput, pValue, pTransactionGUID, pRowIndex);
	}

	/**
	 * Whether a refresh answers from the record the form holds instead of reading the server. The open-refresh
	 * rule `MaterialsEntitySelector._refreshRecord` applies, and the two must agree: a frozen document or a field
	 * with `RefreshOnOpen: false` keeps its saved record. The port adds one case: a value this document open has
	 * already read, or created (`_handleDefaultRecord` marks a new record read), so neither a re-render nor a record
	 * with no server row yet costs a read. A held record without an `ID<Entity>` is still read: the server may have
	 * saved it after the document did.
	 * @param {Record<string, any>} pInput @param {Record<string, any>} pHeldRecord @param {any} pValue
	 * @param {number|string|null} [pRowIndex]
	 * @return {boolean}
	 */
	_keepsHeldRecord(pInput, pHeldRecord, pValue, pRowIndex)
	{
		if (this._isRecordFrozen() || pInput?.PictForm?.RefreshOnOpen === false)
		{
			return true;
		}
		return this._openRefreshes().has(this._openRefreshKey(pInput, pValue, pRowIndex));
	}

	/**
	 * @param {Record<string, any>} pInput @param {any} pValue @param {number|string|null} [pRowIndex]
	 * @return {string} the key a document open records a read under.
	 */
	_openRefreshKey(pInput, pValue, pRowIndex)
	{
		const tmpDocument = this.pict?.AppData?.DocumentData?.ReportData?.GUIDDocument || '';
		return `${tmpDocument}|${this._getRecordAddress(pInput, pRowIndex)}|${pValue}`;
	}

	/** @return {Set<string>} the values read since the document opened (see `_openRefreshKey`). */
	_openRefreshes()
	{
		if (!this._openRefreshedKeys)
		{
			this._openRefreshedKeys = new Set();
		}
		return this._openRefreshedKeys;
	}

	/**
	 * A re-render re-runs `onInputInitialize` for every input in the section. When the value's trigger group already
	 * fired this document open, the re-init must not fire it again: a group whose PostSolvers refresh a table in the
	 * emitter's own section would re-render it, re-init the emitter, and loop without yielding (the local-record
	 * short-circuit answers in a microtask). The `<Entity>Changed` fan-out still runs. Diverges from the legacy
	 * `MaterialsEntitySelector._refreshRecord`, which re-fires on every re-init after a server read.
	 * @param {Record<string, any>} pInput @param {any} pValue @param {number|string|null} pRowIndex
	 */
	_markReinitOfFiredTriggerGroup(pInput, pValue, pRowIndex)
	{
		if (!pInput?.PictForm?.FireTriggerGroupOnRecordRefresh || this._isMultiplePicker(pInput.PictForm))
		{
			return;
		}
		const tmpKey = this._openRefreshKey(pInput, pValue, pRowIndex);
		if (this._firedTriggerGroups().has(tmpKey))
		{
			this._reinitTriggerGroupKeys().add(tmpKey);
		}
	}

	/**
	 * Whether this cascade fires the input's trigger group: false for a re-init of a value whose group already fired
	 * this open (see `_markReinitOfFiredTriggerGroup`); otherwise true, and the value is recorded as fired.
	 * @param {Record<string, any>} pInput @param {Record<string, any>|Array<Record<string, any>>} pRecord
	 * @param {number|string|null} [pRowIndex]
	 * @return {boolean}
	 */
	_claimTriggerGroupFire(pInput, pRecord, pRowIndex)
	{
		const tmpValue = (pRecord && !Array.isArray(pRecord)) ? pRecord[this._getRecordIdentity(pInput)] : undefined;
		if (tmpValue === undefined || tmpValue === null || tmpValue === '')
		{
			return true;
		}
		const tmpKey = this._openRefreshKey(pInput, tmpValue, pRowIndex);
		if (this._reinitTriggerGroupKeys().delete(tmpKey))
		{
			return false;
		}
		this._firedTriggerGroups().add(tmpKey);
		return true;
	}

	/** @return {Set<string>} the values whose trigger group fired since the document opened (see `_openRefreshKey`). */
	_firedTriggerGroups()
	{
		if (!this._firedTriggerGroupKeys)
		{
			this._firedTriggerGroupKeys = new Set();
		}
		return this._firedTriggerGroupKeys;
	}

	/** @return {Set<string>} re-inits waiting on their cascade, which will not fire the trigger group. */
	_reinitTriggerGroupKeys()
	{
		if (!this._reinitTriggerGroupKeySet)
		{
			this._reinitTriggerGroupKeySet = new Set();
		}
		return this._reinitTriggerGroupKeySet;
	}

	/**
	 * Reconcile a refreshed row with the record the form holds — the rule `MaterialsEntitySelector._reconcileRefreshedRecord`
	 * applies, and the two must agree. A re-read of the held record is server state, so the baseline takes the
	 * fields the server moved (`EntityDataBaseline.classifyRecordRefresh`); a held change the server does not
	 * have keeps the held record; a changed selection takes the row. No "out of date" flag: DetectDesync is not
	 * ported to this family.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {any} pHeldRecord
	 * @param {Record<string, any>} pFetchedRecord @param {string} pIdentityField @param {any} pValue
	 * @param {number|string|null} [pRowIndex]
	 * @return {Record<string, any>} the record to write.
	 */
	_reconcileRefreshedRecord(pView, pInput, pHeldRecord, pFetchedRecord, pIdentityField, pValue, pRowIndex)
	{
		if (!this._isScopedEntityDataEnabled() || !pHeldRecord || typeof pHeldRecord !== 'object' || Array.isArray(pHeldRecord) || pHeldRecord[pIdentityField] != pValue)
		{
			return pFetchedRecord;
		}
		const tmpEntity = this._resolveEntity((pInput && pInput.PictForm) || {});
		const tmpGUID = pFetchedRecord[`GUID${tmpEntity}`] || pHeldRecord[`GUID${tmpEntity}`];
		const tmpContext = this._getComprehensionContext(pInput);
		const tmpDecision = EntityDataBaseline.classifyRecordRefresh(this.pict, tmpContext, tmpEntity, tmpGUID, pHeldRecord, pFetchedRecord);
		if (tmpDecision.Action === 'keep')
		{
			this.pict.log.info(`MaterialsCascadePicker: kept the held ${tmpEntity} ${tmpGUID} over the server row: ${tmpDecision.Reason}.`);
			return pHeldRecord;
		}
		if (tmpDecision.Action === 'adopt')
		{
			EntityDataBaseline.adoptRefreshedFields(this.pict, tmpContext, tmpEntity, tmpGUID, pFetchedRecord, tmpDecision.Fields);
		}
		return pFetchedRecord;
	}

	// --- The event bus (the port of the legacy MES base _onRecordRefresh fan-out) ---

	/**
	 * Fan the record refresh out on the cross-widget bus: `<Entity>Changed:<trackingGUID>:<address>`,
	 * plus the opt-in `TriggerGroup:<hash>:RecordRefresh:…` (tabular variant carries the emitting row
	 * index so a dependent picker clears only its own row). Subclasses layer their bespoke cascade on
	 * top and call super for the fan-out.
	 * @param {Object} pView @param {Record<string, any>} pInput
	 * @param {Record<string, any>|Array<Record<string, any>>} pRecord
	 * @param {number|string} [pRowIndex] @param {string} [pTransactionGUID]
	 * @return {void|Promise<any>}
	 */
	_onEntityRecordCascade(pView, pInput, pRecord, pRowIndex, pTransactionGUID)
	{
		super._onEntityRecordCascade(pView, pInput, pRecord, pRowIndex, pTransactionGUID);
		const tmpEntity = this._resolveEntity((pInput && pInput.PictForm) || {});
		const tmpEvent = `${tmpEntity}Changed:${this._getTrackingGUID(pInput, pRowIndex)}:${this._getRecordAddress(pInput, pRowIndex)}`;
		this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(tmpEvent, pTransactionGUID);
		if (pInput.PictForm.FireTriggerGroupOnRecordRefresh && this._claimTriggerGroupFire(pInput, pRecord, pRowIndex))
		{
			const tmpEmitterHash = pInput.Hash || pInput.DataAddress;
			// Hydration guard: this fan-out is a SYNCHRONOUS cascade (the trigger group plus every sibling
			// autofill it emits), and a load is authoritative — persisted values must display, not be re-vetted
			// against the current filter scope. The autofill-scope vet skips while the counter is set. Shared
			// with MaterialsEntitySelector by name so either family suppresses the other. Counter handles
			// re-entrant refreshes; finally guarantees the reset even if a handler throws.
			this.pict._MaterialsEntityHydrationDepth = (this.pict._MaterialsEntityHydrationDepth || 0) + 1;
			try
			{
				if (pRowIndex != null)
				{
					this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(`TriggerGroup:${pInput.PictForm.FireTriggerGroupOnRecordRefresh}:RecordRefresh:${tmpEmitterHash}:${pRowIndex}:${this.pict.getUUID()}`);
				}
				else
				{
					this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(`TriggerGroup:${pInput.PictForm.FireTriggerGroupOnRecordRefresh}:RecordRefresh:${tmpEmitterHash}:${this.pict.getUUID()}`);
				}
			}
			finally
			{
				this.pict._MaterialsEntityHydrationDepth--;
			}
		}
	}

	// --- Cross-widget event reception (the legacy onAfterEvent(Tabular)Completion seam) ---

	/**
	 * The per-subclass event handler. The base handles the save-projection sweep; subclasses override
	 * to handle their targeted events and call super (or replace it — the Sample subclass projects via
	 * its own _handleEvent, idempotently over the same GUID key).
	 * @param {Object} pView @param {Record<string, any>} pInput @param {any} pValue @param {string} pHTMLSelector
	 * @param {number|string|null} pRowIndex @param {string} pEvent @param {string} pTransactionGUID
	 */
	// eslint-disable-next-line no-unused-vars
	_handleEvent(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID)
	{
		this._handleBaseSaveProjectionEvent(pView, pInput, pRowIndex, pEvent, pTransactionGUID);
	}

	// --- The _EntityData save projection (ported from the MES base) ---

	/**
	 * Fire the save-time EntityData projection when this provider has at least one creation-enabled
	 * input (latched in _isEntityCreationEnabled): reset `FormData._EntityData`, broadcast the
	 * ProjectEntityDataRecords sweep inside a transaction so every cascade input projects its created
	 * `__Record` (+ DocumentPolyJoin) into it, then continue the save once the sweep completes.
	 * Providers' onBeforeSaveDataAsync run once each; redundant passes are idempotent (same target,
	 * same GUID keys). No-op — and no empty _EntityData reset — with no creation-enabled input.
	 * @param {(pError?: Error) => void} fCallback
	 */
	onBeforeSaveDataAsync(fCallback)
	{
		if (!this._entityCreationEnabled)
		{
			// @ts-ignore — inherited through the PSP chain tsc cannot see.
			return super.onBeforeSaveDataAsync(fCallback);
		}
		if (!this._isScopedEntityDataEnabled())
		{
			this.pict.manifest.setValueAtAddress(this.pict, 'AppData.DocumentData.ReportData.FormData._EntityData', { });
		}
		const tmpEvent = 'ProjectEntityDataRecords:AppData.DocumentData.ReportData.FormData._EntityData';
		const tmpTransactionGUID = this.pict.getUUID();
		/** @type {any} */ (this.pict).TransactionTracking.registerTransaction(tmpTransactionGUID);
		this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(tmpEvent, tmpTransactionGUID);
		this.pict.views.PictFormMetacontroller.registerOnTransactionCompleteCallback(tmpTransactionGUID, () =>
		{
			this._sanitizeProjectedComprehensions();
			// @ts-ignore — inherited through the PSP chain tsc cannot see.
			super.onBeforeSaveDataAsync(fCallback);
		});
		this.pict.views.PictFormMetacontroller.finalizeTransaction(tmpTransactionGUID);
	}

	/**
	 * The object this input projects its created records into on the save sweep, keyed
	 * `<Entity>.<GUID>`. THE ONE PLACE the scoped/legacy decision is made on the emission side: the
	 * projectors below take the object and are identical on both paths, because a context root and
	 * the legacy ledger have the same shape.
	 *
	 * Scoped roots are remembered for the post-sweep sanitize pass.
	 *
	 * @param {Record<string, any>} pInput - The form input descriptor.
	 * @param {string} pEvent - The sweep event.
	 * @return {Record<string, any>|null} - The projection target, or null when this is not a sweep
	 *   event or no target resolves (never fabricates one).
	 */
	_getProjectionTarget(pInput, pEvent)
	{
		if (typeof pEvent !== 'string')
		{
			return null;
		}
		const [ tmpEventType, tmpTargetAddress ] = pEvent.split(':');
		if (tmpEventType !== 'ProjectEntityDataRecords')
		{
			return null;
		}
		if (this._isScopedEntityDataEnabled())
		{
			const tmpContext = this._getComprehensionContext(pInput);
			const tmpContextRoot = ScopedEntityData.resolveContextRoot(this.pict, tmpContext);
			// A baseline sweep collects into a detached scratch tree: nothing was emitted, so neither the
			// FormEntityCreator cross-check nor the post-sweep delta pass may be armed by it.
			if (tmpContextRoot && !ScopedEntityData.getBaselineSweepTarget(this.pict))
			{
				ScopedEntityData.markActive(this.pict);
				if (!this._scopedProjectionRoots)
				{
					this._scopedProjectionRoots = new Map();
				}
				this._scopedProjectionRoots.set(tmpContext, tmpContextRoot);
				ScopedEntityData.noteProjectionRoot(this.pict, tmpContext, tmpContextRoot);
			}
			return tmpContextRoot;
		}
		if (!tmpTargetAddress)
		{
			return null;
		}
		const tmpTarget = this.pict.manifest.getValueAtAddress(this.pict, tmpTargetAddress);
		if (!tmpTarget || typeof tmpTarget !== 'object' || Array.isArray(tmpTarget))
		{
			return null;
		}
		return tmpTarget;
	}

	/**
	 * Narrow every scoped root this provider projected into during the sweep: strip transient `__` UI
	 * state, then reduce each record to what changed since the baseline. Runs once, after the sweep,
	 * rather than at each write — the projectors key the form's LIVE record objects in, so acting
	 * earlier would either mutate the record the form renders from or force every projector to clone.
	 *
	 * Shares the pass with `MaterialsEntitySelector` so the two families cannot disagree on what a
	 * change is; they sweep one target and a partially-converted family would emit incoherently.
	 */
	_sanitizeProjectedComprehensions()
	{
		// Roots any picker-family input noted on this sweep, not only this provider's: a driver with no
		// inputs of its own still re-projects every other provider's records.
		const tmpRoots = new Map(this._scopedProjectionRoots || []);
		for (const [ tmpContext, tmpContextRoot ] of ScopedEntityData.takeNotedProjectionRoots(this.pict))
		{
			tmpRoots.set(tmpContext, tmpContextRoot);
		}
		if (tmpRoots.size < 1)
		{
			return;
		}
		// Handed to the document save, which folds every provider's emission ONCE just before the PUT.
		// Folding here instead would advance the baseline mid-save, and the next provider's prune over
		// this same root would then suppress every record and send an empty comprehension.
		const tmpEmitted = new Map();
		for (const [ tmpContext, tmpContextRoot ] of tmpRoots)
		{
			EntityDataBaseline.pruneContextRootToDelta(this.pict, tmpContext, tmpContextRoot);
			tmpEmitted.set(tmpContext, tmpContextRoot);
		}
		if (this._scopedProjectionRoots)
		{
			this._scopedProjectionRoots.clear();
		}
		tmpEmitted.forEach((pContextRoot, pContext) => EntityDataBaseline.recordPendingEmission(this.pict, pContext, pContextRoot));
	}

	/**
	 * Ask for the load-settled baseline capture on this render transaction. Idempotent across every
	 * input on the form and across both input families — the first to ask schedules it.
	 * @param {Object} pView @param {string} [pTransactionGUID]
	 */
	_scheduleBaselineCapture(pView, pTransactionGUID)
	{
		if (!this._isScopedEntityDataEnabled())
		{
			return;
		}
		EntityDataBaseline.scheduleLoadBaselineCapture(this.pict, pView, pTransactionGUID);
	}


	/**
	 * Generic save-time projection handler: on the ProjectEntityDataRecords sweep, write this input's
	 * created `__Record` into the shared collection (keyed `[Entity][GUID<Entity>]`), plus any sibling
	 * DocumentPolyJoin. Opt-in via creation; opt-out per input via SkipEntityDataProjection;
	 * _syncRecordToEntityData no-ops unless the record carries its GUID — a second safety gate.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} pRowIndex
	 * @param {string} pEvent @param {string} pTransactionGUID
	 */
	_handleBaseSaveProjectionEvent(pView, pInput, pRowIndex, pEvent, pTransactionGUID)
	{
		if (typeof pEvent !== 'string' || !this._isEntityCreationEnabled(pInput) || pInput?.PictForm?.SkipEntityDataProjection)
		{
			return;
		}
		const tmpTarget = this._getProjectionTarget(pInput, pEvent);
		if (!tmpTarget)
		{
			return;
		}
		this._syncRecordToEntityData(pView, pInput, pRowIndex, pTransactionGUID, tmpTarget);
		const tmpRecordAddress = this._getRecordAddress(pInput, pRowIndex);
		const tmpDocumentPolyJoinAddress = pInput.PictForm?.DocumentPolyJoinAddress || `${tmpRecordAddress}_DocumentPolyJoin`;
		const tmpDocumentPolyJoin = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpDocumentPolyJoinAddress);
		if (tmpDocumentPolyJoin && typeof tmpDocumentPolyJoin === 'object' && !Array.isArray(tmpDocumentPolyJoin) && tmpDocumentPolyJoin.GUIDDocumentPolyJoin)
		{
			if (!tmpTarget.DocumentPolyJoin || typeof tmpTarget.DocumentPolyJoin !== 'object' || Array.isArray(tmpTarget.DocumentPolyJoin))
			{
				tmpTarget.DocumentPolyJoin = { };
			}
			tmpTarget.DocumentPolyJoin[tmpDocumentPolyJoin.GUIDDocumentPolyJoin] = tmpDocumentPolyJoin;
		}
	}

	/**
	 * Whether this provider emits scoped `Document.EntityData` comprehensions instead of the legacy
	 * `FormData._EntityData` ledger. The same flag stands `FormEntityCreator` down, so the two
	 * paths never run together.
	 * @return {boolean}
	 */
	_isScopedEntityDataEnabled()
	{
		return ScopedEntityData.isEnabled(this.pict);
	}

	/**
	 * The trigger context this input's records are applied under. Scheduling is manifest data:
	 * `PictForm.ComprehensionContext` selects it, the provider default (`OnSave`) applies otherwise.
	 * @param {Record<string, any>} pInput
	 * @return {string}
	 */
	_getComprehensionContext(pInput)
	{
		return ScopedEntityData.resolveContext(pInput, this.options?.ComprehensionContext || ScopedEntityData.DEFAULT_CONTEXT);
	}

	/** A committed picker value that means "nothing selected". @param {any} pValue @return {boolean} */
	_isClearedPickerValue(pValue)
	{
		return (pValue == null) || (pValue === '') || (pValue === 'null') || (pValue === 'undefined');
	}


	/**
	 * A creation-enabled selection was cleared. Two things follow, both only under the scoped path:
	 * a tombstone for the record IF it is known to exist server-side (never for one that has not
	 * been saved), and a FRESH `__Salt` so a re-add mints a NEW GUID — the apply cannot un-delete a
	 * soft-deleted row, because `Deleted` is stripped at marshal, so re-using the GUID would upsert
	 * into the tombstoned row. No-op unless the cleared record actually carried its client GUID.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} [pRowIndex]
	 */
	_handleEntitySelectionCleared(pView, pInput, pRowIndex)
	{
		if (!this._isScopedEntityDataEnabled() || !this._isEntityCreationEnabled(pInput))
		{
			return;
		}
		const tmpEntity = this._resolveEntity((pInput && pInput.PictForm) || {});
		const tmpRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), this._getRecordAddress(pInput, pRowIndex));
		const tmpGUID = (tmpRecord && typeof tmpRecord === 'object' && !Array.isArray(tmpRecord)) ? tmpRecord[`GUID${tmpEntity}`] : null;
		if (!tmpGUID)
		{
			return;
		}
		this._stageRemovalTombstones(pInput, this._collectRemovedDependentRecords(pView, pInput, pRowIndex, new Set()), 'clear');
		this._generateSaltIfNeeded(pView, pInput, pRowIndex, true);
	}

	/**
	 * Stage tombstones for a set of removed records and report what happened. Every removal route in
	 * this family funnels through here, so the gates (persisted, document-owned) and the logging are
	 * applied once. A record the guard would refuse is logged at debug and NOT staged — see
	 * `docs/plans/entitydata-round2/05-client-delete-completeness.md` §4.
	 * @param {Record<string, any>} pInput @param {Array<{ Entity: string, Record: Record<string, any> }>} pRemovals
	 * @param {string} pReason - What removed them, for the log line.
	 * @return {number} - How many tombstones were staged.
	 */
	_stageRemovalTombstones(pInput, pRemovals, pReason)
	{
		if (!this._isScopedEntityDataEnabled() || !Array.isArray(pRemovals) || pRemovals.length < 1)
		{
			return 0;
		}
		const tmpContext = this._getComprehensionContext(pInput);
		const tmpResult = ScopedEntityData.tombstoneRemovedRecords(this.pict, tmpContext, pRemovals);
		if (tmpResult.Staged.length > 0)
		{
			this.pict.log.info(`MaterialsCascadePicker[${pInput.Hash || pInput.DataAddress}]: staged deletion of ${tmpResult.Staged.length} record(s) on ${pReason}.`, { Records: tmpResult.Staged });
		}
		return tmpResult.Staged.length;
	}

	/**
	 * The identity values a committed selection retains, as a Set of strings.
	 * @param {any} pValue @return {Set<string>}
	 */
	_getRetainedIdentitySet(pValue)
	{
		const tmpRetained = new Set();
		if (pValue == null || pValue === '')
		{
			return tmpRetained;
		}
		for (const tmpToken of String(pValue).split(','))
		{
			const tmpTrimmed = tmpToken.trim();
			if (tmpTrimmed.length > 0)
			{
				tmpRetained.add(tmpTrimmed);
			}
		}
		return tmpRetained;
	}

	/**
	 * Records this input's SUBCLASS holds that belong to a de-selected value. The base holds none.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} pRowIndex
	 * @param {Set<string>} pRetainedIdentities
	 * @return {Array<{ Entity: string, Record: Record<string, any> }>}
	 */
	// eslint-disable-next-line no-unused-vars
	_collectRemovedDependentRecords(pView, pInput, pRowIndex, pRetainedIdentities)
	{
		if (pRetainedIdentities.size > 0)
		{
			return [ ];
		}
		const tmpAddress = pInput.PictForm?.DocumentPolyJoinAddress || `${this._getRecordAddress(pInput, pRowIndex)}_DocumentPolyJoin`;
		return ScopedEntityData.collectTestInstanceUnit({ DocumentPolyJoin: this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpAddress) });
	}

	/**
	 * The de-selection seam — the Picker twin of `MaterialsEntitySelector._handleSelectionRemovals`,
	 * and asserted equivalent to it by the parity contract. Suppressed during a programmatic load,
	 * because a loader that seeds a value re-enters here indistinguishably from a user de-selection.
	 * Anything removed also rotates `__Salt`, exactly as the selector does.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {any} pValue @param {number|string|null} [pRowIndex]
	 */
	_handleSelectionRemovals(pView, pInput, pValue, pRowIndex)
	{
		if (!this._isScopedEntityDataEnabled() || ScopedEntityData.isProgrammaticLoad(this.pict))
		{
			return;
		}
		const tmpRetained = this._getRetainedIdentitySet(pValue);
		const tmpRemovals = this._collectRemovedDependentRecords(pView, pInput, pRowIndex, tmpRetained);
		this._stageRemovalTombstones(pInput, tmpRemovals, 'de-selection');
		if (tmpRemovals.length > 0)
		{
			this._generateSaltIfNeeded(pView, pInput, pRowIndex, true);
		}
	}

	/**
	 * Project this input's `__Record` into the entity-data collection, keyed `[Entity][GUID<Entity>]`.
	 * No-op unless the record carries its client GUID (only created/creating records project).
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} pRowIndex
	 * @param {string} pTransactionGUID @param {Record<string, Record<string, any>>} [pTargetObject]
	 */
	_syncRecordToEntityData(pView, pInput, pRowIndex, pTransactionGUID, pTargetObject)
	{
		const tmpEntity = this._resolveEntity((pInput && pInput.PictForm) || {});
		const tmpRecordAddress = this._getRecordAddress(pInput, pRowIndex);
		const tmpRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpRecordAddress);
		if (!tmpRecord || typeof tmpRecord !== 'object' || Array.isArray(tmpRecord) || !tmpRecord[`GUID${tmpEntity}`])
		{
			return;
		}
		const tmpEntityData = pTargetObject;
		if (!tmpEntityData || typeof tmpEntityData !== 'object' || Array.isArray(tmpEntityData))
		{
			return;
		}
		if (!tmpEntityData[tmpEntity] || typeof tmpEntityData[tmpEntity] !== 'object' || Array.isArray(tmpEntityData[tmpEntity]))
		{
			tmpEntityData[tmpEntity] = { };
		}
		tmpEntityData[tmpEntity][tmpRecord[`GUID${tmpEntity}`]] = tmpRecord;
	}

	/**
	 * Whether `_handleEvent` runs for this event while the event is being raised (`onEvent`) instead of
	 * once its transaction has completed. An event whose handler registers async loads on the transaction
	 * must be handled here: pict-section-form deletes a transaction right after running its completion
	 * callbacks, so a load registered from one of them is orphaned and the form settles before it lands.
	 * @param {string} pEvent
	 * @return {boolean}
	 */
	_handlesEventOnTrigger(pEvent)
	{
		if (typeof pEvent !== 'string')
		{
			return false;
		}
		return this._getTriggerTimeEventTypes().includes(pEvent.split(':')[0]);
	}

	/**
	 * The event types `_handleEvent` handles while they are raised (see `_handlesEventOnTrigger`).
	 * @return {Array<string>}
	 */
	_getTriggerTimeEventTypes()
	{
		return [ ];
	}

	onEvent(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID)
	{
		if (this._handlesEventOnTrigger(pEvent))
		{
			this._handleEvent(pView, pInput, pValue, pHTMLSelector, null, pEvent, pTransactionGUID);
		}
		// @ts-ignore — inherited through the PSP chain tsc cannot see.
		return super.onEvent(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID);
	}

	onEventTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID)
	{
		if (this._handlesEventOnTrigger(pEvent))
		{
			this._handleEvent(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID);
		}
		// @ts-ignore — inherited through the PSP chain tsc cannot see.
		return super.onEventTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID);
	}

	onAfterEventCompletion(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID)
	{
		if (!this._handlesEventOnTrigger(pEvent))
		{
			this._handleEvent(pView, pInput, pValue, pHTMLSelector, null, pEvent, pTransactionGUID);
		}
		// @ts-ignore — inherited through the PSP chain tsc cannot see.
		return super.onAfterEventCompletion(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID);
	}

	onAfterEventTabularCompletion(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID)
	{
		if (!this._handlesEventOnTrigger(pEvent))
		{
			this._handleEvent(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID);
		}
		// @ts-ignore — inherited through the PSP chain tsc cannot see.
		return super.onAfterEventTabularCompletion(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID);
	}
}

module.exports = PictProviderInputTypeMaterialsCascadePicker;

module.exports.default_configuration = _DEFAULT_CONFIGURATION;
module.exports.NEW_ENTITY_ID = NEW_ENTITY_ID;
